import { facesToRoofFaces } from "building-generation/elements/roofFaceNew";
import { buildRoofPerimeterRails } from "building-generation/elements/roofs/roofPerimeterRails";
import { getFirstSlope, isRoofEdgeAlignedToSide } from "building-generation/elements/roofs/roofSlopes";
import { getExtraHipFaces } from "building-generation/elements/roofs/roofTruncation";
import type { GableRoofExtrusion, GableRoofVerticalRails } from "building-types/roofs/gableRoofExtrusionTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import { RoofFaceType } from "building-types/roofs/roofFace";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { RoofElevationPlanSettings, RoofPlanSettings } from "building-types/settings/roofPlanSettings";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { copyBSP, addPolygonsToBSP, addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { addEdgeToPolygonList, clippingNodeNew, clipFace, isOverlappingFace } from "geom/bspClipping";
import type { PolygonBSP } from "geom/bspTypes";
import { BSP_EPSILON } from "geom/bspTypes";
import { planeBSPFromPlane } from "geom/planeBSP";
import { polyLine2EdgeCount } from "geom/polyLine2";
import { offsetRoofPerimeter } from "../../roofs/roofPerimeters";
import { makeUpperFloorLimitBSP } from "../../roofTools/upperFloorLimitBSP";

/**
 * Generates the roof top faces
 */
export function generateRoofVolumeTopFaces({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    id: subRoofId,
    baseHeight,
    roofType,
    leftSideSlopes,
    rightSideSlopes,
    roofSettings,
    heelHeightOffsets,
    perimeters,
    isDormer,
    extensions,
    shedExtensions,
    verticalRails,
    shedRoofHalfFace,
    extrusion,
    exactEdges,
    edges,
    overhangs,
    rectangularOverhangs,
    blocks,
    rails,
    startFaces,
    endFaces,
    roofPlanSettings,
    roofElevationPlanSettings,
  } = roofVolume;

  const { peakRail } = rails;
  const { hasStartExtension, hasEndExtension } = extensions;
  const { boundingPerimeter, offsetWallPerimeter, startEdge } = perimeters;
  const { edgeOverhangs, limitEdgeOverhangs, limitEdgeNoOverhangs } = overhangs;
  const { baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;

  const {
    roofData,
    inputPerimeters,
    roofInputSettings,
    clipContext,
    upperFloorPerimeters = {},
    upperFloorCeilElevation = baseHeight,
    issues,
  } = roofVolumeOptions;

  const { wallSettings } = roofInputSettings;

  const { roofEdgeOverrides } = roofData;

  const { limitPerimeter } = inputPerimeters;
  const { overlapBSP } = clipContext;

  const { slope: firstSlopeLeft } = getFirstSlope(leftSideSlopes);
  const { slope: firstSlopeRight } = getFirstSlope(rightSideSlopes);
  const wallExtensionHeightLeft = firstSlopeLeft * baseLeftOverhang;
  const wallExtensionHeightRight = firstSlopeRight * baseRightOverhang;

  // upper floor clipping
  const upperFloorBSP = makeUpperFloorLimitBSP({
    boundingPerimeter,
    upperFloorPerimeters,
    edgeOverhangs,
    wallSettings,
    baseHeight,
    upperFloorCeilElevation,
    heelHeightOffset: heelHeightOffsets.refOffset,
  });

  let limitBSP = copyBSP(upperFloorBSP);
  let limitNoOverhangBSP = copyBSP(limitBSP);

  // build detail perimeter bsp
  if (limitPerimeter) {
    const isInvalidLimitEdge = (edgeIndex: number): boolean => {
      if (polyLine2EdgeCount(limitPerimeter) > 4) {
        return false;
      }

      const isStartAligned = isRoofEdgeAlignedToSide({
        boundingPerimeter,
        startEdge,
        exactPerimeter: limitPerimeter,
        edgeIndex,
        side: RoofSide.START,
      });

      const isEndAligned = isRoofEdgeAlignedToSide({
        boundingPerimeter,
        startEdge,
        exactPerimeter: limitPerimeter,
        edgeIndex,
        side: RoofSide.END,
      });

      return (isStartAligned && hasStartExtension) || (isEndAligned && hasEndExtension);
    };

    let offsetLimitPerimeter = offsetRoofPerimeter(limitPerimeter, limitEdgeOverhangs);

    const limitPolygons: PolygonBSP[] = [];
    for (let i = 0, l = polyLine2EdgeCount(offsetLimitPerimeter); i < l; i++) {
      if (isInvalidLimitEdge(i)) {
        continue;
      }

      addEdgeToPolygonList(offsetLimitPerimeter, i, 0.0, 1.0, null, limitPolygons, MULTIGABLE_BSP_EPSILON);
    }

    limitBSP = addPolygonsToBSP(limitBSP, null, limitPolygons, BSP_EPSILON, -1);

    offsetLimitPerimeter = offsetRoofPerimeter(limitPerimeter, limitEdgeNoOverhangs);

    const limitNoOverhangPolygons: PolygonBSP[] = [];
    for (let i = 0, l = polyLine2EdgeCount(offsetLimitPerimeter); i < l; i++) {
      if (isInvalidLimitEdge(i)) {
        continue;
      }

      addEdgeToPolygonList(offsetLimitPerimeter, i, 0.0, 1.0, null, limitNoOverhangPolygons, MULTIGABLE_BSP_EPSILON);
    }

    limitNoOverhangBSP = addPolygonsToBSP(limitNoOverhangBSP, null, limitNoOverhangPolygons, BSP_EPSILON, -1);
  }

  const clipTopFacesOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(limitBSP)],
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const removeTopFacesOverlapOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(overlapBSP)],
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const clipTopFaceWithOptions = (topFace: RoofFace, options: ClipFaceContext): RoofFace[] => {
    const { face, type, side, isDormer, annotationSettings } = topFace;

    const clippedFaces = clipFace({
      face,
      options,
    });

    return facesToRoofFaces(clippedFaces, type, side, isDormer, annotationSettings);
  };

  const clipTopFacesWithOptions = (topFaces: RoofFace[], options: ClipFaceContext): RoofFace[] => {
    const clippedTopFaces: RoofFace[] = [];

    for (const topFace of topFaces) {
      clippedTopFaces.push(...clipTopFaceWithOptions(topFace, options));
    }

    return clippedTopFaces;
  };

  const clipTopFace = (topFace: RoofFace, removeOverlap: boolean): RoofFace[] => {
    const { face } = topFace;
    let clippedTopFaces = [topFace];

    // remove overlap
    if (removeOverlap && isOverlappingFace(face, overlapBSP, MULTIGABLE_BSP_EPSILON)) {
      clippedTopFaces = clipTopFaceWithOptions(topFace, removeTopFacesOverlapOptions);
    }

    return clipTopFacesWithOptions(clippedTopFaces, clipTopFacesOptions);
  };

  const clipTopFaces = (topFaces: RoofFace[], removeOverlap: boolean): RoofFace[] => {
    const clippedTopFaces: RoofFace[] = [];

    for (const topFace of topFaces) {
      clippedTopFaces.push(...clipTopFace(topFace, removeOverlap));
    }

    return clippedTopFaces;
  };

  // collect the base roof top faces
  const unclippedRoofTopFaces = collectRoofTopFaces({
    roofType,
    extrusion,
    verticalRails,
    isDormer,
    annotationSettings: { ...roofPlanSettings, ...roofElevationPlanSettings },
  });

  // clip them
  let roofTopFaces = clipTopFaces(unclippedRoofTopFaces, true);
  let roofTopFacesWithOverlap = clipTopFaces(unclippedRoofTopFaces, false);

  // build temporary roof rails
  const roofPerimeterRails = buildRoofPerimeterRails({
    subRoofId,
    exactEdges,
    edges,
    roofSettings,
    wallSettings,
    extensions,
    shedExtensions,
    roofTopFaces,
    peakRail,
    isDormer,
  });

  // collect the extra hip faces
  const extraHipFaces = getExtraHipFaces({
    subRoofId,
    roofEdgeOverrides,
    height: baseHeight + Math.min(wallExtensionHeightLeft, wallExtensionHeightRight),
    perimeter: offsetWallPerimeter,
    blocks,
    perimeters,
    baseHeight,
    roofPerimeterRails,
    exactEdges,
    extrusion,
    hasStartExtension,
    hasEndExtension,
    startFrontFace,
    endFrontFace,
    shedRoofHalfFace,
    annotationSettings: { ...roofPlanSettings, ...roofElevationPlanSettings },
    issues,
  });

  // clip top faces with extra hip faces
  const customHipOptions: ClipFaceContext = {
    clippingNodes: extraHipFaces.map((x) =>
      clippingNodeNew(addConvexPlanesToBSPLeaf([planeBSPFromPlane(x.face.plane)]), true),
    ),
    coplanarsToFront: false,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  roofTopFaces = clipTopFacesWithOptions(roofTopFaces, customHipOptions);
  roofTopFacesWithOverlap = clipTopFacesWithOptions(roofTopFacesWithOverlap, customHipOptions);

  // add extra hip faces
  roofTopFaces = [...extraHipFaces, ...roofTopFaces];

  return {
    roofVolume: {
      unclippedRoofTopFaces,
      roofTopFaces,
      roofTopFacesWithOverlap,
      limitBSP,
      limitNoOverhangBSP,
      upperFloorBSP,
    },
    temporaries: {
      extraHipFaces,
    },
  };
}

interface CollectRoofTopFacesOptions {
  readonly roofType: GableRoofType;
  readonly extrusion: GableRoofExtrusion;
  readonly verticalRails: GableRoofVerticalRails;
  readonly isDormer: boolean;
  readonly annotationSettings: RoofPlanSettings & RoofElevationPlanSettings;
}

function collectRoofTopFaces({
  roofType,
  extrusion,
  verticalRails,
  isDormer,
  annotationSettings,
}: CollectRoofTopFacesOptions): RoofFace[] {
  const { isStartTruncated, isStartFullyTruncated, isEndTruncated, isEndFullyTruncated } = verticalRails;

  const roofFaces: RoofFace[] = [];

  if (roofType !== GableRoofType.LEFT_SIDE_SHED_ROOF) {
    roofFaces.push(
      ...facesToRoofFaces(extrusion.rightFaces, RoofFaceType.NORMAL, RoofSide.RIGHT, isDormer, annotationSettings),
    );
  }

  if (roofType !== GableRoofType.RIGHT_SIDE_SHED_ROOF) {
    roofFaces.push(
      ...facesToRoofFaces(extrusion.leftFaces, RoofFaceType.NORMAL, RoofSide.LEFT, isDormer, annotationSettings),
    );
  }

  if (isEndTruncated) {
    roofFaces.push(
      ...facesToRoofFaces(
        extrusion.endHipFaces,
        isEndFullyTruncated ? RoofFaceType.HIP : RoofFaceType.TRUNCATED,
        RoofSide.END,
        isDormer,
        annotationSettings,
      ),
    );
  }

  if (isStartTruncated) {
    roofFaces.push(
      ...facesToRoofFaces(
        extrusion.startHipFaces,
        isStartFullyTruncated ? RoofFaceType.HIP : RoofFaceType.TRUNCATED,
        RoofSide.START,
        isDormer,
        annotationSettings,
      ),
    );
  }

  return roofFaces.filter((x) => !!x.face);
}
